import { hmac } from '@noble/hashes/hmac.js'
import { sha256 } from '@noble/hashes/sha2.js'

// A player's check-in pass, rotating like a stadium ticket: the code changes
// every period, is computed on the phone (no signal needed at the door), and
// the API only accepts the current step, give or take one. A screenshot is
// dead within seconds. Mirrors api/internal/checkin/token.go — the shared test
// vector in pass-code.test.ts keeps the two in lockstep.

/** What /check-in/mine hands the phone for one tournament's pass. */
export interface RotatingPass {
  /** Per-player, per-tournament secret (base64url). Only this pass holder gets it. */
  seed: string
  /** FrontendURL + "/pass/v2." — the code is appended to it. */
  code_prefix: string
  season_id: string
  user_id: string
  period_seconds: number
  /** The API's clock when it answered (unix seconds), to correct a phone that's off. */
  server_time: number
}

const encoder = new TextEncoder()

/** Unpadded base64url, the encoding the API uses on both sides of the code. */
export function base64url(bytes: Uint8Array): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0)
    out += alphabet[(n >> 18) & 63] + alphabet[(n >> 12) & 63]
    if (i + 1 < bytes.length) out += alphabet[(n >> 6) & 63]
    if (i + 2 < bytes.length) out += alphabet[n & 63]
  }
  return out
}

export function fromBase64url(text: string): Uint8Array {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
  const bytes: number[] = []
  let buffer = 0
  let bits = 0
  for (const ch of text) {
    const v = alphabet.indexOf(ch)
    if (v < 0) throw new Error('not base64url')
    buffer = (buffer << 6) | v
    bits += 6
    if (bits >= 8) {
      bits -= 8
      bytes.push((buffer >> bits) & 0xff)
    }
  }
  return new Uint8Array(bytes)
}

/** A UUID's 16 raw bytes. */
export function uuidBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '')
  if (!/^[0-9a-f]{32}$/i.test(hex)) throw new Error(`not a UUID: ${uuid}`)
  const out = new Uint8Array(16)
  for (let i = 0; i < 16; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

/** Which rotation step a moment falls in. */
export function passStep(unixSeconds: number, periodSeconds: number): number {
  return Math.floor(unixSeconds / periodSeconds)
}

/** Milliseconds until the code changes — what the countdown bar shows. */
export function msUntilNextStep(nowMs: number, periodSeconds: number): number {
  const periodMs = periodSeconds * 1000
  return periodMs - (nowMs % periodMs)
}

/** The QR payload (a URL) for a given moment. */
export function passCode(pass: RotatingPass, unixSeconds: number): string {
  const step = passStep(unixSeconds, pass.period_seconds)
  const raw = new Uint8Array(36)
  raw.set(uuidBytes(pass.season_id), 0)
  raw.set(uuidBytes(pass.user_id), 16)
  new DataView(raw.buffer).setUint32(32, step >>> 0, false)
  const body = base64url(raw)
  const mac = hmac(sha256, fromBase64url(pass.seed), encoder.encode(`v2.${body}`)).slice(0, 16)
  return `${pass.code_prefix}${body}.${base64url(mac)}`
}

/**
 * How far the phone's clock is from the API's, in seconds, measured when the
 * pass was fetched. Added to the phone's time so a clock a minute off still
 * shows a code the desk accepts.
 */
export function clockOffset(serverTime: number, localMsAtFetch: number): number {
  return serverTime - Math.floor(localMsAtFetch / 1000)
}
